import { useEffect, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import {
  getCommodityBidMe,
  getCompletedCommodityBidItems,
  kindBadgeClass,
  kindLabel,
} from '@/services/commoditybidIntegration';
import CommodityBidItemDetails from '@/components/customer/CommodityBidItemDetails';

function formatDateShort(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatAmount(item) {
  if (item.amount == null || item.amount === '') return '—';
  const n = Number(item.amount);
  if (Number.isNaN(n)) return String(item.amount);
  return `${item.currency === 'USD' ? '$' : ''}${n.toLocaleString()} ${item.currency !== 'USD' ? item.currency : ''}`.trim();
}

const StatusBadge = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const map = {
    pending: 'bg-amber-50 text-amber-700',
    open: 'bg-amber-50 text-amber-700',
    active: 'bg-sky-50 text-sky-700',
    accepted: 'bg-green-50 text-green-700',
    rejected: 'bg-red-50 text-red-700',
    closed: 'bg-slate-100 text-slate-600',
    completed: 'bg-slate-100 text-slate-600',
    cancelled: 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${map[s] || 'bg-slate-50 text-slate-600'}`}>
      {status}
    </span>
  );
};

function HistoryItemCard({ item }) {
  const isRequest = item.kind === 'production_request';

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-5"
      data-testid={`history-${item.kind}-${item.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${kindBadgeClass(item.kind)}`}
            >
              {kindLabel(item.kind)}
            </span>
            <StatusBadge status={item.status} />
          </div>
          <p className="font-heading text-lg font-bold text-slate-900">{item.title}</p>
          {!isRequest && item.supplierName && (
            <p className="mt-0.5 text-sm text-slate-600">{item.supplierName}</p>
          )}
        </div>
        <div className="shrink-0 text-right text-sm">
          {!isRequest && <p className="font-bold text-slate-900">{formatAmount(item)}</p>}
          <p className="text-xs text-slate-400">{formatDateShort(item.createdAt)}</p>
        </div>
      </div>
      <CommodityBidItemDetails item={item} />
    </div>
  );
}

export default function BidHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await getCommodityBidMe();
        if (cancelled) return;
        setItems(getCompletedCommodityBidItems(res.data));
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-5" data-testid="bid-history-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Completed Bids</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Closed production requests and resolved supplier bids
        </p>
      </div>

      {loading && (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          <p className="text-sm text-slate-500">Loading history…</p>
        </div>
      )}

      {!loading && loadError && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-center text-sm text-red-800">
          CommodityBid data could not be loaded.
        </div>
      )}

      {!loading && !loadError && items.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center">
          <History className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="font-medium text-slate-500">No completed bids yet</p>
        </div>
      )}

      {!loading && !loadError && items.length > 0 && (
        <div className="grid gap-4" data-testid="bid-history-list">
          {items.map((item) => (
            <HistoryItemCard key={`${item.kind}-${item.id}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
